import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import { Counter } from "../components/site/Counter";
import { BackgroundGrid } from "../components/site/RouteLine";
import { SectionHeading } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";

export default function AboutAmanda() {
  return (
    <div data-testid="about-page">
      <Seo title="Meet Amanda Rand — Founder & CEO" description="Amanda Rand founded Next Mile Compliance on faith, transparency, education, and accountability — bringing 10 years of trucking experience and 4 years of hands-on dispatch to help carriers build real businesses." />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <BackgroundGrid className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal><p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">About the founder</p></Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]">MEET <span className="nm-gold-text">AMANDA RAND.</span></h1>
            </Reveal>
            <Reveal delay={0.2}><p className="mt-3 text-gray-400 font-semibold">Founder & CEO · Next Mile Compliance LLC</p></Reveal>
            <Reveal delay={0.3}>
              <p className="mt-7 text-xl text-gray-200 leading-relaxed italic">
                "For me, trucking has never just been about moving freight. It's about helping people build something that can change their lives."
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative">
              <div className="absolute -inset-3 nm-gold-bg rounded-3xl opacity-20 blur-2xl" />
              <img src={IMAGES.founder} alt="Amanda Rand, Founder & CEO" className="relative rounded-3xl w-full object-cover aspect-[4/5] max-h-[560px]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-16 bg-[#0B132B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 grid grid-cols-2 gap-6">
          {[{ n: 10, l: "Trucking Industry Experience", u: "YEARS" }, { n: 4, l: "Hands-On Dispatch Experience", u: "YEARS" }].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.12}>
              <div className="nm-card rounded-2xl p-8 text-center">
                <div className="font-head text-6xl font-black nm-gold-text"><Counter to={s.n} /></div>
                <p className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] mt-1">{s.u}</p>
                <p className="text-gray-400 text-sm mt-3">{s.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="relative py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6 text-gray-300 leading-relaxed">
          <Reveal><p>Over the years, Amanda has worked with trucking companies, drivers, owner-operators, new authorities, and entrepreneurs who had the vision to build a business but needed someone to help them understand what happens behind the scenes.</p></Reveal>
          <Reveal><p>She believes information shouldn't be held hostage. When she knows something that can help another person move forward, she wants to teach it. Next Mile Compliance is built on <span className="text-white font-semibold">faith, transparency, education, and accountability.</span></p></Reveal>

          <Reveal>
            <div className="my-10 relative nm-card rounded-2xl p-8 border-l-4 border-[#D4AF37]">
              <Quote className="text-[#D4AF37]/40 absolute top-5 right-6" size={40} />
              <p className="font-head text-2xl sm:text-3xl font-extrabold text-white leading-tight">"I won't sell you a dream just to collect your money."</p>
            </div>
          </Reveal>

          <Reveal><p>Amanda believes in being transparent about the trucking industry and helping clients identify what needs to change to strengthen their businesses. Her faith is at the center of how she operates — she believes God gives people gifts not just for themselves, but so they can help others grow.</p></Reveal>
        </div>
      </section>

      {/* Closing */}
      <section className="relative py-20 bg-[#0B132B] overflow-hidden">
        <BackgroundGrid className="opacity-20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-head text-2xl sm:text-4xl font-extrabold text-white leading-tight">
            WE'RE HELPING PEOPLE BUILD BUSINESSES WITH STRUCTURE, KNOWLEDGE, INTEGRITY, AND PURPOSE — <span className="nm-gold-text">ONE MILE AT A TIME.</span>
          </motion.p>
          <p className="mt-6 font-mono text-sm tracking-widest text-[#D4AF37]">— AMANDA RAND</p>
        </div>
      </section>

      <CTABand title="Let's build your business — one mile at a time." button="GET STARTED" />
    </div>
  );
}
